import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Search, X, MapPin, Mountain, Map as MapIcon, ArrowRight,
  Tent, Anchor, Footprints, Video, Calendar, BookOpen, Route, FileText,
} from "lucide-react";
import { useLocations, useNationalParks, useRegions, useFestivals } from "@/hooks/use-travel";
import { useAppStore } from "@/store/use-app-store";
import { localePath, SiteLang } from "@/lib/locale";
import { t5 } from "@/lib/i18n";
import { normalizeSearch } from "@/lib/normalize";
import { getLocalizedLocationName } from "@shared/location-name-translations";
import { CAMPSITES } from "@shared/camping";
import { MARINAS, SAILING_ROUTES } from "@shared/sailing";
import { HIKING_TRAILS } from "@shared/hiking-data";
import { WEBCAM_DATA } from "@shared/webcam-data";
import { ISLANDS_DATA } from "@/data/islands";
import { wikiSize } from "@/lib/wiki-image";
import type { EditorialGuideRow, ItineraryBlogRow, Festival } from "@shared/schema";

function thumbUrl(url: string | null | undefined, width: number): string {
  if (!url) return "";
  if (url.startsWith("/api/")) return url;
  if (url.includes("wikimedia.org") && /\/\d+px-/.test(url)) {
    return url.replace(/\/\d+px-/, `/${width}px-`);
  }
  return url;
}

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  initialQuery?: string;
}

type ResultKind =
  | "location" | "park" | "region" | "island"
  | "festival" | "guide" | "itinerary" | "blog"
  | "campsite" | "marina" | "route" | "trail" | "webcam";

type BlogSummary = {
  id: number;
  title: string;
  slug: string;
  slugDe: string | null;
  slugFr: string | null;
  slugIt: string | null;
  slugNl: string | null;
  slugHr: string | null;
  slugPl: string | null;
  metaDescription: string | null;
  imageUrl: string | null;
  category: string | null;
  topic: string | null;
  readTime: number;
  translations: Record<string, { title?: string; metaDescription?: string }> | null;
};

interface ResultItem {
  key: string;
  kind: ResultKind;
  name: string;
  subtitle: string;
  image: string | null;
  href: string;
  testId: string;
  score: number;
}

const PER_KIND_LIMIT = 5;

function score(query: string, primary: string, others: ReadonlyArray<string | undefined | null>): number {
  const p = normalizeSearch(primary);
  const q = query;
  if (p === q) return 100;
  if (p.startsWith(q)) return 80;
  if (p.includes(q)) return 60;
  for (const o of others) {
    if (!o) continue;
    if (normalizeSearch(o).includes(q)) return 40;
  }
  return 0;
}

export function SearchModal({ open, onClose, initialQuery = "" }: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();
  const { language } = useAppStore();
  const lang = language as SiteLang;

  // Database-backed datasets — only fetched while the modal is open.
  const { data: locations } = useLocations({ enabled: open });
  const { data: parks } = useNationalParks({ enabled: open });
  const { data: regions } = useRegions({ enabled: open });
  const { data: festivals } = useFestivals({ enabled: open });

  const { data: guides } = useQuery<EditorialGuideRow[]>({
    queryKey: ["/api/editorial-guides"],
    queryFn: async () => {
      const res = await fetch("/api/editorial-guides", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch editorial guides");
      return res.json();
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: itineraryBlogs } = useQuery<ItineraryBlogRow[]>({
    queryKey: ["/api/itinerary-blogs"],
    queryFn: async () => {
      const res = await fetch("/api/itinerary-blogs", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch itinerary blogs");
      return res.json();
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const q = normalizeSearch(query.trim());

  // Debounce the query so we don't hit /api/blog/search on every keystroke.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 280);
    return () => clearTimeout(t);
  }, [q]);

  // Server-side blog search — never loads all posts client-side.
  const { data: blogResults = [] } = useQuery<BlogSummary[]>({
    queryKey: ["/api/blog/search", debouncedQ],
    queryFn: () => fetch(`/api/blog/search?q=${encodeURIComponent(debouncedQ)}`).then(r => r.json()),
    enabled: open && debouncedQ.length >= 2,
    staleTime: 30 * 1000,
  });

  const catLabel = useCallback((cat: string): string => {
    const map: Record<string, [string, string, string, string, string, string]> = {
      Beach:    ["Strand",       "Plage",       "Spiaggia",    "Strand",    "Plaža",    "Plaża"],
      City:     ["Stadt",        "Ville",       "Città",       "Stad",      "Grad",     "Miasto"],
      Nature:   ["Natur",        "Nature",      "Natura",      "Natuur",    "Priroda",  "Natura"],
      Mountain: ["Berg",         "Montagne",    "Montagna",    "Berg",      "Planina",  "Góra"],
      Island:   ["Insel",        "Île",         "Isola",       "Eiland",    "Otok",     "Wyspa"],
      Camping:  ["Camping",      "Camping",     "Campeggio",   "Kamperen",  "Kampiranje","Kemping"],
      Forest:   ["Wald",         "Forêt",       "Foresta",     "Bos",       "Šuma",     "Las"],
      River:    ["Fluss",        "Rivière",     "Fiume",       "Rivier",    "Rijeka",   "Rzeka"],
    };
    const tr = map[cat];
    if (!tr) return cat;
    if (lang === "de") return tr[0];
    if (lang === "fr") return tr[1];
    if (lang === "it") return tr[2];
    if (lang === "nl") return tr[3];
    if (lang === "hr") return tr[4];
    if (lang === "pl") return tr[5];
    return cat;
  }, [lang]);

  // Section labels
  const labels = useMemo(() => ({
    destinations:    t5(language, "Destinations", "Reiseziele", "Destinations", "Destinazioni", "Bestemmingen", "Destinacije", "Destynacje"),
    parks:           t5(language, "National Parks", "Nationalparks", "Parcs nationaux", "Parchi nazionali", "Nationale parken", "Nacionalni parkovi", "Parki Narodowe"),
    regions:         t5(language, "Regions", "Regionen", "Régions", "Regioni", "Regio's", "Regije", "Regiony"),
    festivals:       t5(language, "Festivals & Events", "Festivals & Veranstaltungen", "Festivals & événements", "Festival ed eventi", "Festivals & evenementen", "Festivali i događanja", "Festiwale i wydarzenia"),
    guides:          t5(language, "Travel Guides", "Reiseführer", "Guides de voyage", "Guide di viaggio", "Reisgidsen", "Putni vodiči", "Przewodniki podróżne"),
    itineraries:     t5(language, "Itineraries", "Reiserouten", "Itinéraires", "Itinerari", "Reisroutes", "Planovi putovanja", "Trasy podróży"),
    blogs:           t5(language, "Blog Articles", "Blog-Artikel", "Articles de blog", "Articoli del blog", "Blogartikelen", "Blog članci", "Artykuły blogowe"),
    campsites:       t5(language, "Campsites", "Campingplätze", "Campings", "Campeggi", "Campings", "Kampovi", "Kempingi"),
    marinas:         t5(language, "Marinas", "Yachthäfen", "Marinas", "Marine", "Jachthavens", "Marine", "Mariny"),
    routes:          t5(language, "Sailing Routes", "Segelrouten", "Itinéraires de voile", "Rotte di vela", "Zeilroutes", "Jedriličarske rute", "Trasy żeglarskie"),
    trails:          t5(language, "Hiking Trails", "Wanderwege", "Sentiers de randonnée", "Sentieri", "Wandelpaden", "Planinarske staze", "Szlaki piesze"),
    webcams:         t5(language, "Webcams", "Webcams", "Webcams", "Webcam", "Webcams", "Web kamere", "Kamery internetowe"),
    festivalLabel:   t5(language, "Festival", "Festival", "Festival", "Festival", "Festival", "Festival", "Festiwal"),
    guideLabel:      t5(language, "Guide", "Reiseführer", "Guide", "Guida", "Gids", "Vodič", "Przewodnik"),
    itineraryLabel:  t5(language, "Itinerary", "Reiseroute", "Itinéraire", "Itinerario", "Reisroute", "Plan putovanja", "Trasa"),
    blogLabel:       t5(language, "Blog", "Blog", "Blog", "Blog", "Blog", "Blog", "Blog"),
    campsiteLabel:   t5(language, "Campsite", "Campingplatz", "Camping", "Campeggio", "Camping", "Kamp", "Kemping"),
    marinaLabel:     t5(language, "Marina", "Yachthafen", "Marina", "Marina", "Jachthaven", "Marina", "Marina"),
    routeLabel:      t5(language, "Sailing Route", "Segelroute", "Itinéraire de voile", "Rotta di vela", "Zeilroute", "Jedriličarska ruta", "Trasa żeglarska"),
    trailLabel:      t5(language, "Hiking Trail", "Wanderweg", "Sentier", "Sentiero", "Wandelpad", "Planinarska staza", "Szlak"),
    webcamLabel:     t5(language, "Live Webcam", "Live-Webcam", "Webcam en direct", "Webcam dal vivo", "Live webcam", "Web kamera uživo", "Kamera na żywo"),
    nationalPark:    t5(language, "National Park", "Nationalpark", "Parc national", "Parco nazionale", "Nationaal park", "Nacionalni park", "Park Narodowy"),
    region:          t5(language, "Region", "Region", "Région", "Regione", "Regio", "Regija", "Region"),
    islands:         t5(language, "Islands", "Inseln", "Îles", "Isole", "Eilanden", "Otoci", "Wyspy"),
    islandLabel:     t5(language, "Island", "Insel", "Île", "Isola", "Eiland", "Otok", "Wyspa"),
  }), [language]);

  // Build all matches.
  const allResults = useMemo<ResultItem[]>(() => {
    if (q.length < 1) return [];
    const out: ResultItem[] = [];

    // Locations
    for (const l of locations ?? []) {
      const localized = getLocalizedLocationName(l.name, language);
      const s = score(q, l.name, [
        localized, l.descriptionEn, l.descriptionDe, l.category, l.slug,
      ]);
      if (s > 0) {
        out.push({
          key: `loc-${l.id}`,
          kind: "location",
          name: localized,
          subtitle: catLabel(l.category),
          image: l.image,
          href: localePath(`/location/${l.slug}`, lang),
          testId: `search-result-location-${l.slug}`,
          score: s,
        });
      }
    }

    // National Parks
    for (const p of parks ?? []) {
      const s = score(q, p.name, [p.descriptionEn, p.descriptionDe, p.slug]);
      if (s > 0) {
        out.push({
          key: `park-${p.id}`,
          kind: "park",
          name: p.name,
          subtitle: labels.nationalPark,
          image: p.image,
          href: localePath(`/national-park/${p.slug}`, lang),
          testId: `search-result-park-${p.slug}`,
          score: s,
        });
      }
    }

    // Islands (static — Croatian island guide pages)
    for (const isl of ISLANDS_DATA) {
      const tagline = isl.tagline[lang] ?? isl.tagline.en;
      const intro = isl.intro[lang] ?? isl.intro.en;
      const townNames = isl.towns.map((tw) => tw.name).join(" ");
      const s = score(q, isl.name, [tagline, intro, isl.slug, isl.region, townNames]);
      if (s > 0) {
        out.push({
          key: `island-${isl.slug}`,
          kind: "island",
          name: isl.name,
          subtitle: `${labels.islandLabel} · ${isl.region === "dalmatia" ? "Dalmatia" : "Kvarner"}`,
          image: wikiSize(isl.heroImage, 200),
          href: localePath(`/islands/${isl.slug}`, lang),
          testId: `search-result-island-${isl.slug}`,
          score: s,
        });
      }
    }

    // Regions
    for (const r of regions ?? []) {
      const s = score(q, r.name, [r.descriptionEn, r.descriptionDe, r.slug]);
      if (s > 0) {
        out.push({
          key: `region-${r.id}`,
          kind: "region",
          name: r.name,
          subtitle: labels.region,
          image: r.image ?? null,
          href: localePath(`/region/${r.slug}`, lang),
          testId: `search-result-region-${r.slug}`,
          score: s,
        });
      }
    }

    // Festivals
    for (const f of (festivals ?? []) as Festival[]) {
      if (!f.slug) continue;
      const tr = (f.translations ?? null) as Record<string, { description?: string; highlight?: string }> | null;
      const others: Array<string | null | undefined> = [
        f.description, f.highlight, f.locationDisplay, f.type, f.datesDisplay,
      ];
      if (tr) {
        for (const v of Object.values(tr)) {
          if (v?.description) others.push(v.description);
          if (v?.highlight) others.push(v.highlight);
        }
      }
      const s = score(q, f.name, others);
      if (s > 0) {
        out.push({
          key: `fest-${f.id}`,
          kind: "festival",
          name: f.name,
          subtitle: `${labels.festivalLabel} · ${f.locationDisplay}`,
          image: null,
          href: localePath(`/festivals/${f.slug}`, lang),
          testId: `search-result-festival-${f.slug}`,
          score: s,
        });
      }
    }

    // Editorial Guides
    for (const g of guides ?? []) {
      const localizedTitle = pickGuideTitle(g, lang);
      const s = score(q, localizedTitle, [
        g.title, g.titleDe, g.titleFr, g.titleIt, g.titleNl, g.titleHr, g.titlePl,
        g.description, g.descriptionDe, g.descriptionFr, g.descriptionIt, g.descriptionNl, g.descriptionHr, g.descriptionPl,
        g.category, g.slug,
      ]);
      if (s > 0) {
        out.push({
          key: `guide-${g.id}`,
          kind: "guide",
          name: localizedTitle,
          subtitle: g.category || labels.guideLabel,
          image: g.coverImage,
          href: localePath(`/guides/${g.slug}`, lang),
          testId: `search-result-guide-${g.slug}`,
          score: s,
        });
      }
    }

    // Itinerary Blogs
    for (const b of itineraryBlogs ?? []) {
      const localizedTitle = pickItineraryTitle(b, lang);
      const s = score(q, localizedTitle, [
        b.title, b.titleDe, b.titleFr, b.titleIt, b.titleNl, b.titleHr, b.titlePl,
        b.description, b.descriptionDe, b.descriptionFr, b.descriptionIt, b.descriptionNl, b.descriptionHr, b.descriptionPl,
        ...(b.tags ?? []), b.slug,
      ]);
      if (s > 0) {
        out.push({
          key: `itin-${b.id}`,
          kind: "itinerary",
          name: localizedTitle,
          subtitle: `${labels.itineraryLabel} · ${b.duration} ${t5(language, "days", "Tage", "jours", "giorni", "dagen", "dana", "dni")}`,
          image: b.coverImage,
          href: localePath(`/itinerary-guide/${b.slug}`, lang),
          testId: `search-result-itinerary-${b.slug}`,
          score: s,
        });
      }
    }

    // Campsites (static)
    for (const c of CAMPSITES) {
      const s = score(q, c.name, [
        c.descriptionEn, c.descriptionDe, c.regionName, c.nearestTown, c.category,
      ]);
      if (s > 0) {
        out.push({
          key: `camp-${c.id}`,
          kind: "campsite",
          name: c.name,
          subtitle: `${labels.campsiteLabel} · ${c.nearestTown}`,
          image: c.image,
          href: `${localePath('/camping', lang)}#campsite-${c.id}`,
          testId: `search-result-campsite-${c.id}`,
          score: s,
        });
      }
    }

    // Marinas (static)
    for (const m of MARINAS) {
      const s = score(q, m.name, [m.town, m.regionSlug]);
      if (s > 0) {
        out.push({
          key: `marina-${m.id}`,
          kind: "marina",
          name: m.name,
          subtitle: `${labels.marinaLabel} · ${m.town}`,
          image: m.image,
          href: `${localePath('/sailing', lang)}#marina-${m.id}`,
          testId: `search-result-marina-${m.id}`,
          score: s,
        });
      }
    }

    // Sailing routes (static)
    for (const r of SAILING_ROUTES) {
      const s = score(q, r.name, [
        r.descriptionEn, r.descriptionDe, r.descriptionFr, r.descriptionIt, r.descriptionNl,
        r.startPoint, r.endPoint, r.gygSearchTerm,
      ]);
      if (s > 0) {
        out.push({
          key: `route-${r.id}`,
          kind: "route",
          name: r.name,
          subtitle: `${labels.routeLabel} · ${r.startPoint} → ${r.endPoint}`,
          image: null,
          href: `${localePath('/sailing', lang)}#route-${r.id}`,
          testId: `search-result-route-${r.id}`,
          score: s,
        });
      }
    }

    // Hiking trails (static)
    for (const t of HIKING_TRAILS) {
      const localizedName =
        lang === "de" ? t.nameDe :
        lang === "fr" ? t.nameFr :
        lang === "it" ? t.nameIt :
        lang === "nl" ? t.nameNl :
        t.name;
      const s = score(q, localizedName, [
        t.name, t.nameDe, t.nameFr, t.nameIt, t.nameNl,
        t.description, t.descriptionDe, t.descriptionFr, t.descriptionIt, t.descriptionNl,
        t.region, t.locationSlug,
      ]);
      if (s > 0) {
        out.push({
          key: `trail-${t.id}`,
          kind: "trail",
          name: localizedName,
          subtitle: `${labels.trailLabel} · ${t.distanceKm} km`,
          image: null,
          href: `${localePath('/hiking', lang)}#trail-${t.id}`,
          testId: `search-result-trail-${t.id}`,
          score: s,
        });
      }
    }

    // Webcams (static)
    for (const w of WEBCAM_DATA) {
      const localizedName =
        lang === "de" ? w.nameDe :
        lang === "fr" ? w.nameFr :
        lang === "it" ? w.nameIt :
        lang === "nl" ? w.nameNl :
        w.nameEn;
      const s = score(q, localizedName, [
        w.nameEn, w.nameDe, w.nameFr, w.nameIt, w.nameNl,
        w.region, w.category, w.locationSlug,
      ]);
      if (s > 0) {
        out.push({
          key: `cam-${w.id}`,
          kind: "webcam",
          name: localizedName,
          subtitle: `${labels.webcamLabel} · ${w.locationSlug}`,
          image: null,
          href: `${localePath('/webcams', lang)}#webcam-${w.id}`,
          testId: `search-result-webcam-${w.id}`,
          score: s,
        });
      }
    }

    // AI Blog Posts — already filtered by the server; apply client score for ranking.
    for (const b of blogResults) {
      const localizedTitle = b.translations?.[lang]?.title ?? b.title;
      const s = score(q, localizedTitle, [
        b.title, b.metaDescription, b.category, b.topic,
        b.translations?.[lang]?.metaDescription,
      ]);
      out.push({
        key: `blog-${b.id}`,
        kind: "blog",
        name: localizedTitle,
        subtitle: `${labels.blogLabel}${b.category ? ` · ${b.category}` : ""}`,
        image: b.imageUrl,
        href: (() => { const slugMap: Record<string, string | null | undefined> = { de: b.slugDe, fr: b.slugFr, it: b.slugIt, nl: b.slugNl, hr: b.slugHr, pl: b.slugPl }; const localSlug = lang !== "en" ? (slugMap[lang] ?? b.slug) : b.slug; return localePath(`/blog/${localSlug}`, lang); })(),
        testId: `search-result-blog-${b.slug}`,
        score: Math.max(s, 40),
      });
    }

    return out;
  }, [q, blogResults, locations, parks, regions, festivals, guides, itineraryBlogs, lang, language, catLabel, labels]);

  // Group + cap per kind, then flatten in display order.
  const KIND_ORDER: ResultKind[] = useMemo(() => [
    "island", "location", "park", "region",
    "festival", "guide", "itinerary", "blog",
    "campsite", "trail", "marina", "route", "webcam",
  ], []);

  const grouped = useMemo(() => {
    const groups = new Map<ResultKind, ResultItem[]>();
    for (const k of KIND_ORDER) groups.set(k, []);
    for (const r of allResults) {
      groups.get(r.kind)?.push(r);
    }
    for (const k of KIND_ORDER) {
      const arr = groups.get(k)!;
      arr.sort((a, b) => b.score - a.score);
      groups.set(k, arr.slice(0, PER_KIND_LIMIT));
    }
    return groups;
  }, [allResults, KIND_ORDER]);

  const flatResults = useMemo(() => {
    const out: ResultItem[] = [];
    for (const k of KIND_ORDER) out.push(...(grouped.get(k) ?? []));
    return out;
  }, [grouped, KIND_ORDER]);

  const handleSelect = useCallback((href: string) => {
    onClose();
    setQuery("");
    // Defer navigation slightly so the modal close animation isn't preempted
    // and so the destination's hash-scroll effect runs after route mount.
    setTimeout(() => navigate(href), 0);
  }, [navigate, onClose]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, initialQuery]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        if (flatResults.length === 0) return;
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === "ArrowUp") {
        if (flatResults.length === 0) return;
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && flatResults.length > 0) {
        e.preventDefault();
        const clamped = Math.min(Math.max(selectedIndex, 0), flatResults.length - 1);
        const target = flatResults[clamped];
        if (target?.href) handleSelect(target.href);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, flatResults, selectedIndex, handleSelect, onClose]);

  if (!open) return null;

  const placeholder = t5(language,
    "Search destinations, blogs, festivals, guides, camps...",
    "Reiseziele, Blogs, Festivals, Guides, Camps suchen...",
    "Rechercher destinations, blogs, festivals, guides, camps...",
    "Cerca destinazioni, blog, festival, guide, campeggi...",
    "Zoek bestemmingen, blogs, festivals, gidsen, kampen...",
    "Pretraži odredišta, blogove, festivale, vodiče, kampove...",
    "Szukaj destynacji, blogów, festiwali, przewodników, kempingów...");

  const emptyHint = t5(language,
    "Search destinations, blog articles, festivals, guides, beaches, camps, marinas, hikes, webcams…",
    "Suche in Reisezielen, Blog-Artikeln, Festivals, Guides, Stränden, Camps, Marinas, Wanderungen, Webcams…",
    "Recherchez destinations, articles de blog, festivals, guides, plages, camps, marinas, randonnées, webcams…",
    "Cerca destinazioni, articoli del blog, festival, guide, spiagge, campeggi, marine, escursioni, webcam…",
    "Zoek bestemmingen, blogartikelen, festivals, gidsen, stranden, kampen, jachthavens, wandelingen, webcams…",
    "Pretražite odredišta, blog članke, festivale, vodiče, plaže, kampove, marine, planinarenje, kamere…",
    "Szukaj destynacji, artykułów blogowych, festiwali, przewodników, plaż, kempingów, marin, szlaków, kamer…");

  const noResults = t5(language,
    "No results found",
    "Keine Ergebnisse gefunden",
    "Aucun résultat trouvé",
    "Nessun risultato trovato",
    "Geen resultaten gevonden"
  , "Nema pronađenih rezultata", "Nie znaleziono wyników");

  const sectionMeta: Record<ResultKind, { label: string; icon: React.ReactNode }> = {
    island:    { label: labels.islands,      icon: <MapPin className="w-3 h-3" /> },
    location:  { label: labels.destinations, icon: <MapPin className="w-3 h-3" /> },
    park:      { label: labels.parks,        icon: <Mountain className="w-3 h-3" /> },
    region:    { label: labels.regions,      icon: <MapIcon className="w-3 h-3" /> },
    festival:  { label: labels.festivals,    icon: <Calendar className="w-3 h-3" /> },
    guide:     { label: labels.guides,       icon: <BookOpen className="w-3 h-3" /> },
    itinerary: { label: labels.itineraries,  icon: <Route className="w-3 h-3" /> },
    blog:      { label: labels.blogs,        icon: <FileText className="w-3 h-3" /> },
    campsite:  { label: labels.campsites,    icon: <Tent className="w-3 h-3" /> },
    marina:    { label: labels.marinas,      icon: <Anchor className="w-3 h-3" /> },
    route:     { label: labels.routes,       icon: <Route className="w-3 h-3" /> },
    trail:     { label: labels.trails,       icon: <Footprints className="w-3 h-3" /> },
    webcam:    { label: labels.webcams,      icon: <Video className="w-3 h-3" /> },
  };

  let runningIndex = 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col sm:items-center sm:justify-start sm:pt-[10vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t5(language, "Search", "Suche", "Rechercher", "Cerca", "Zoeken", "Pretraži", "Szukaj")}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="search-backdrop"
      />

      <div className="relative w-full sm:max-w-2xl mx-auto flex flex-col bg-background sm:rounded-xl shadow-2xl border border-border/60 overflow-hidden max-h-screen sm:max-h-[80vh]">
        <div className="flex items-center gap-3 px-4 h-16 border-b border-border/40">
          <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-base text-foreground placeholder:text-muted-foreground outline-none min-w-0"
            data-testid="input-global-search"
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
              data-testid="button-clear-search"
              aria-label={t5(language, "Clear search", "Suche löschen", "Effacer la recherche", "Cancella ricerca", "Zoekopdracht wissen", "Očistite pretraživanje", "Wyczyść wyszukiwanie")}
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="ml-1 text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded text-sm font-medium"
            data-testid="button-close-search"
            aria-label={t5(language, "Close search", "Suche schließen", "Fermer la recherche", "Chiudi ricerca", "Zoeken sluiten", "Zatvori pretraživanje", "Zamknij wyszukiwanie")}
          >
            Esc
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {q.length === 0 && (
            <div className="py-12 px-6 text-center text-muted-foreground text-sm">
              <Search className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p>{emptyHint}</p>
            </div>
          )}

          {q.length > 0 && flatResults.length === 0 && (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <p>{noResults}</p>
            </div>
          )}

          {flatResults.length > 0 && (
            <ul role="listbox" className="py-2">
              {KIND_ORDER.map((kind) => {
                const items = grouped.get(kind) ?? [];
                if (items.length === 0) return null;
                const meta = sectionMeta[kind];
                return (
                  <div key={kind}>
                    <li className="px-4 pt-3 pb-1">
                      <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
                        {meta.icon}
                        {meta.label}
                      </p>
                    </li>
                    {items.map((item) => {
                      const idx = runningIndex++;
                      return (
                        <ResultRow
                          key={item.key}
                          name={item.name}
                          subtitle={item.subtitle}
                          image={item.image}
                          href={item.href}
                          selected={selectedIndex === idx}
                          onSelect={handleSelect}
                          onHover={() => setSelectedIndex(idx)}
                          testId={item.testId}
                          icon={meta.icon}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </ul>
          )}
        </div>

        {flatResults.length > 0 && (
          <div className="border-t border-border/40 px-4 py-2 flex items-center gap-4 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">↑↓</kbd>
              {t5(language, "navigate", "navigieren", "naviguer", "navigare", "navigeren", "navigirati", "nawiguj")}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">↵</kbd>
              {t5(language, "open", "öffnen", "ouvrir", "apri", "openen", "otvori", "otwórz")}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">Esc</kbd>
              {t5(language, "close", "schließen", "fermer", "chiudi", "sluiten", "zatvori", "zamknij")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function pickGuideTitle(g: EditorialGuideRow, lang: SiteLang): string {
  if (lang === "de" && g.titleDe) return g.titleDe;
  if (lang === "fr" && g.titleFr) return g.titleFr;
  if (lang === "it" && g.titleIt) return g.titleIt;
  if (lang === "nl" && g.titleNl) return g.titleNl;
  if (lang === "hr" && g.titleHr) return g.titleHr;
  if (lang === "pl" && g.titlePl) return g.titlePl;
  return g.title;
}

function pickItineraryTitle(b: ItineraryBlogRow, lang: SiteLang): string {
  if (lang === "de" && b.titleDe) return b.titleDe;
  if (lang === "fr" && b.titleFr) return b.titleFr;
  if (lang === "it" && b.titleIt) return b.titleIt;
  if (lang === "nl" && b.titleNl) return b.titleNl;
  if (lang === "hr" && b.titleHr) return b.titleHr;
  if (lang === "pl" && b.titlePl) return b.titlePl;
  return b.title;
}

interface ResultRowProps {
  name: string;
  subtitle: string;
  image: string | null;
  href: string;
  selected: boolean;
  onSelect: (href: string) => void;
  onHover: () => void;
  testId: string;
  icon: React.ReactNode;
}

function ResultRow({ name, subtitle, image, href, selected, onSelect, onHover, testId, icon }: ResultRowProps) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${selected ? "bg-primary/10 text-foreground" : "hover:bg-muted text-foreground"}`}
        onClick={() => onSelect(href)}
        onMouseMove={onHover}
        data-testid={testId}
      >
        <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-muted relative">
          {image ? (
            <>
              <div className="absolute inset-0 bg-muted animate-pulse" aria-hidden="true" />
              <img
                src={thumbUrl(image, 200)}
                alt=""
                className="w-full h-full object-cover relative z-10 transition-opacity duration-200"
                loading="eager"
                decoding="async"
                onLoad={(e) => { e.currentTarget.style.opacity = "1"; }}
                style={{ opacity: 0 }}
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = "none";
                  const skeleton = target.previousElementSibling as HTMLElement | null;
                  if (skeleton) skeleton.style.display = "none";
                  const fallback = target.nextElementSibling as HTMLElement | null;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
              <div
                className="w-full h-full absolute inset-0 z-20 items-center justify-center text-muted-foreground"
                style={{ display: "none" }}
              >
                {icon}
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              {icon}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{name}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
            {icon}
            <span className="truncate">{subtitle}</span>
          </p>
        </div>
        <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-opacity ${selected ? "opacity-70 text-primary" : "opacity-0"}`} />
      </button>
    </li>
  );
}
